/**
 * Quick Buy — mobile Add-to-Cart bottom sheet.
 *
 * Replaces the card-anchored quick-add overlay on mobile for `picker` products.
 * product-card.js calls window.NotedQuickBuy.open(card) below 768px; here we:
 *   1. fetch the product's buy box via the Section Rendering API
 *      (/products/{handle}?section_id=quick-buy) — the SAME snippets the PDP
 *      renders (variant picker, threshold bar, Kite GWP mount, buy buttons);
 *   2. inject it into the global sheet shell (snippets/quick-buy-sheet);
 *   3. wire size selection · qty · Add to Basket.
 *
 * Add → serialize the whole product form (keeps line-item props / bundles) →
 * dispatch `cart:adding` (cart.js opens the drawer) + the bundled cart-drawer
 * section render → `cart:change`, then close the sheet so the drawer (z 60)
 * slides over it (z 55). threshold-bar.js / cart-gwp.js self-render the injected
 * progress bar + GWP via their own document.body observers — nothing to init.
 */
(function () {
  'use strict';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  var sheet = null, bodyEl = null, lastTrigger = null, closeTimer = null;

  function resolve() {
    if (!sheet) {
      sheet = document.querySelector('[data-quick-buy-sheet]');
      if (sheet) bodyEl = sheet.querySelector('[data-quick-buy-body]');
    }
    return sheet;
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function setLoading(on) { if (sheet) sheet.classList.toggle('is-loading', !!on); }

  // Hide the rest of the page from AT + keyboard while the sheet is open.
  function setBackgroundInert(on) {
    var kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (el === sheet) continue;
      if (on) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
      else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
    }
  }

  /* ───────────── Open / close ───────────── */

  function open(card) {
    if (!resolve() || !card) return;
    var url = card.getAttribute('data-product-url');
    if (!url) return;
    lastTrigger = card.querySelector('[data-card-trigger]') || card;

    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    bodyEl.innerHTML = '';

    // Slide up immediately with a spinner; fill when the fetch lands.
    sheet.hidden = false;
    setLoading(true);
    void sheet.offsetWidth; // reflow so the transition runs from the closed state
    sheet.classList.add('is-open');
    document.body.classList.add('noted-sheet-open');
    setBackgroundInert(true);

    fetch(url.split('?')[0] + '?section_id=quick-buy', { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var content = tmp.querySelector('[data-quick-buy-content]');
        bodyEl.innerHTML = '';
        if (content) bodyEl.appendChild(content); else bodyEl.innerHTML = html;
        setLoading(false);
        wire(bodyEl);
        // Render the in-sheet free-gift picker (Kite won't on a collection page).
        if (window.NotedQuickBuyGwp) {
          var gwpEl = bodyEl.querySelector('[data-quick-buy-gwp]');
          if (gwpEl) window.NotedQuickBuyGwp.render(gwpEl);
        }
        var closeBtn = sheet.querySelector('[data-quick-buy-close]:not(.quick-buy-sheet__backdrop)');
        if (closeBtn) try { closeBtn.focus(); } catch (e) {}
      })
      .catch(function (err) {
        console.error('[quick-buy] section fetch failed', err);
        setLoading(false);
        close();
      });
  }

  function close() {
    if (!sheet || !sheet.classList.contains('is-open')) return;
    sheet.classList.remove('is-open');
    document.body.classList.remove('noted-sheet-open');
    setBackgroundInert(false);
    if (lastTrigger) { try { lastTrigger.focus(); } catch (e) {} lastTrigger = null; }
    var delay = reducedMotion() ? 0 : 360;
    closeTimer = setTimeout(function () { if (sheet) sheet.hidden = true; }, delay);
  }

  /* ───────────── Wire the injected buy box ───────────── */

  function wire(scope) {
    var idInput = scope.querySelector('[data-variant-id-input]');
    var buttons = Array.prototype.slice.call(scope.querySelectorAll('[data-variant-button]'));

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('data-variant-available') === 'false') return;
        buttons.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (idInput) idInput.value = btn.getAttribute('data-variant-id');
        var pf = btn.getAttribute('data-variant-price-formatted');
        if (pf) scope.querySelectorAll('[data-atc-price]').forEach(function (el) { el.textContent = pf; });
      });
    });

    // Quantity stepper
    var qty = scope.querySelector('[data-qty-input]');
    var minus = scope.querySelector('[data-qty-minus]');
    var plus = scope.querySelector('[data-qty-plus]');
    function clampQty() {
      var n = parseInt(qty.value, 10);
      if (isNaN(n) || n < 1) n = 1;
      qty.value = n;
      return n;
    }
    if (qty && minus) minus.addEventListener('click', function () { qty.value = Math.max(1, clampQty() - 1); });
    if (qty && plus) plus.addEventListener('click', function () { qty.value = clampQty() + 1; });

    // Add to basket
    var form = scope.querySelector('form[id^="product-form-"], form[action$="/cart/add"]');
    if (form) {
      form.addEventListener('submit', function (e) { e.preventDefault(); addToCart(form, qty); });
    }
  }

  function addToCart(form, qty) {
    var atcButtons = Array.prototype.slice.call(form.querySelectorAll('[data-atc]'));
    atcButtons.forEach(function (b) { b.disabled = true; });
    form.querySelectorAll('[data-atc-label]').forEach(function (el) { el.textContent = (window.notedStrings && window.notedStrings.product.adding) || ''; });

    // Serialize the whole form so line-item properties / bundle selections reach
    // /cart/add.js (mirrors assets/main-product.js). Browser sets the multipart
    // boundary for FormData — do NOT set Content-Type.
    var formData = new FormData(form);
    if (qty) {
      var n = parseInt(qty.value, 10);
      formData.set('quantity', isNaN(n) || n < 1 ? 1 : n);
    }
    // Bundled section rendering: return the cart-drawer section in the same
    // response so the drawer renders in one round-trip (cart.js reads it).
    var drawer = document.querySelector('[data-cart-drawer]');
    if (drawer && drawer.dataset.sectionId) {
      formData.set('sections', drawer.dataset.sectionId);
      formData.set('sections_url', window.location.pathname);
    }

    // Open the drawer (spinner) and hand off — close the sheet so the drawer,
    // which un-inerts as the sheet does, slides over the closing sheet.
    document.dispatchEvent(new CustomEvent('cart:adding'));
    close();

    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) throw res.data;
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { sections: res.data.sections } }));
      })
      .catch(function (err) {
        console.error('[quick-buy] add to basket failed', err);
        document.dispatchEvent(new CustomEvent('cart:add-error', {
          detail: { message: (err && (err.description || err.message)) || '' }
        }));
      });
  }

  /* ───────────── Close interactions ───────────── */

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-quick-buy-close]')) { e.preventDefault(); close(); }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheet && sheet.classList.contains('is-open')) close();
  });

  window.NotedQuickBuy = { open: open, close: close };
})();
